import { cssString, escapeHtml, formatCprForPrint } from '@/lib/referral/referralPrint';

// T2.2 (GGOP-M 2.6, 2.8, 3.1–3.9): a GGOP is shown and printed with MedCom's own
// stylesheet (Referencer/GGOP/html: viewEmessage.xsl → common.xsl → formatted_text.xsl,
// rehabilitationplan2html.xsl; XSLT 1.0, run by the browser's XSLTProcessor), so all
// information in MedCom's html view is found in Clinio. Browsers do not reliably resolve
// xsl:include, so the four files are merged into one stylesheet here. Two runtime patches,
// both for GGOP-M 3.7/3.8: RelationCode is also read in the 1.0.4 namespace (the only one
// the published XSD accepts) and `vaerge` is shown as "Værge" (MedCom's list predates it).
// The XML comes from communicate-ggop with every CPR replaced by GGOP_CPR_MARKER; the
// patient's CPR is put back only after an audited decrypt-cpr.

export const GGOP_XSL_BASE = '/medcom/ggop/';
export const GGOP_XSL_MAIN = 'viewEmessage.xsl';
export const GGOP_XSL_FILES = ['viewEmessage.xsl', 'common.xsl', 'formatted_text.xsl', 'rehabilitationplan2html.xsl'] as const;
export const GGOP_CPR_MARKER = '##CPR##';

/** Every namespace prefix any of the four stylesheets uses in XPath. */
const PREFIXES: Record<string, string> = {
  mc: 'urn:oio:medcom:municipality:1.0.0',
  mm100: 'urn:oio:medcom:municipality:1.0.0',
  mm101: 'urn:oio:medcom:municipality:1.0.1',
  mm102: 'urn:oio:medcom:municipality:1.0.2',
  mm104: 'urn:oio:medcom:municipality:1.0.4',
  xs: 'http://www.w3.org/2001/XMLSchema',
};

const STYLESHEET_OPEN = /<xsl:stylesheet\b[^>]*>/;
const INCLUDE = /<xsl:include\s+href="([^"]+)"\s*\/>/g;

function stylesheetBody(source: string): string {
  const open = STYLESHEET_OPEN.exec(source);
  const end = source.lastIndexOf('</xsl:stylesheet>');
  if (!open || end < 0) throw new Error('Ugyldigt stylesheet');
  return source.slice(open.index + open[0].length, end).replace(/<xsl:output\b[^>]*\/>/g, '');
}

/**
 * One stylesheet from MedCom's four: each xsl:include is replaced by the included file's
 * templates (recursively), and the root declares every prefix used in their XPaths.
 */
export function mergeGgopStylesheets(files: Readonly<Record<string, string>>, main: string = GGOP_XSL_MAIN): string {
  const seen = new Set<string>();
  const inline = (body: string): string =>
    body.replace(INCLUDE, (_m, href: string) => {
      if (seen.has(href)) return '';
      seen.add(href);
      const source = files[href];
      if (source === undefined) throw new Error(`Mangler ${href}`);
      return inline(stylesheetBody(source));
    });
  const source = files[main];
  if (source === undefined) throw new Error(`Mangler ${main}`);
  seen.add(main);
  const open = STYLESHEET_OPEN.exec(source);
  if (!open) throw new Error('Ugyldigt stylesheet');
  let root = open[0].replace(/\sversion="[^"]*"/, ' version="1.0"');
  for (const [prefix, uri] of Object.entries(PREFIXES)) {
    if (!new RegExp(`xmlns:${prefix}=`).test(root)) root = root.replace(/>$/, ` xmlns:${prefix}="${uri}">`);
  }
  const body = inline(stylesheetBody(source));
  return patchRelationCode(`<?xml version="1.0" encoding="UTF-8"?>\n${root}<xsl:output method="html" indent="yes"/>${body}</xsl:stylesheet>`);
}

/** GGOP-M 3.7/3.8: RelationCode in 1.0.0 or 1.0.4, and "Værge". */
export function patchRelationCode(xsl: string): string {
  return xsl
    .replace(/<xsl:for-each select="mc:RelationCode">/g, '<xsl:for-each select="mc:RelationCode | mm104:RelationCode">')
    .replace(
      /(<xsl:when test="text\(\) = 'øvrig_familie'">Øvrig familie<\/xsl:when>)/g,
      `$1<xsl:when test="text() = 'vaerge'">Værge</xsl:when><xsl:when test="text() = 'ingen_relationer'">Ingen relationer</xsl:when>`,
    );
}

let cached: Promise<string> | null = null;

/** The merged stylesheet, fetched once from the app's own /medcom/ggop/. */
export function loadGgopStylesheet(fetcher: typeof fetch = fetch): Promise<string> {
  if (!cached) {
    cached = Promise.all(
      GGOP_XSL_FILES.map(async (name) => {
        const res = await fetcher(`${GGOP_XSL_BASE}${name}`);
        if (!res.ok) throw new Error(`Kunne ikke hente ${name}`);
        return [name, await res.text()] as const;
      }),
    ).then((pairs) => mergeGgopStylesheets(Object.fromEntries(pairs)));
    cached.catch(() => {
      cached = null;
    });
  }
  return cached;
}

/** The XML with the CPR marker replaced (10 digits shown as ddmmåå-xxxx) or a placeholder. */
export function withGgopCpr(xml: string, cpr: string | null, placeholder: string): string {
  const value = cpr ? formatCprForPrint(cpr) : placeholder;
  return xml.split(GGOP_CPR_MARKER).join(escapeHtml(value));
}

export type GgopRendered = { css: string; html: string };

/** Runs MedCom's stylesheet; returns its own CSS and the plan's HTML (no scripts, no external links). */
export function renderGgop(xml: string, xsl: string): GgopRendered {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml.replace(/^\uFEFF/, ''), 'application/xml');
  const xslDoc = parser.parseFromString(xsl, 'application/xml');
  if (xmlDoc.getElementsByTagName('parsererror').length > 0) throw new Error('Genoptræningsplanen kan ikke læses');
  if (xslDoc.getElementsByTagName('parsererror').length > 0) throw new Error('MedComs stylesheet kan ikke læses');
  if (typeof XSLTProcessor === 'undefined') throw new Error('Browseren kan ikke køre MedComs stylesheet (XSLT er slået fra)');
  const processor = new XSLTProcessor();
  processor.importStylesheet(xslDoc);
  const out = processor.transformToDocument(xmlDoc);
  if (!out || !out.documentElement) throw new Error('MedComs stylesheet gav intet resultat');
  out.querySelectorAll('script, link, iframe, object, embed').forEach((el) => el.remove());
  // A browser's native XSLT may prepend its own deprecation banner; it is not part of the plan.
  out.querySelectorAll('body > div').forEach((el) => {
    if (/^This site uses XSLT/.test((el.textContent ?? '').trim())) el.remove();
  });
  out.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    }
  });
  const css = Array.from(out.querySelectorAll('style')).map((s) => s.textContent ?? '').join('\n');
  const body = out.querySelector('body');
  return { css, html: body ? body.innerHTML : out.documentElement.outerHTML };
}

/**
 * The handful of Bootstrap 3 rules MedCom's markup relies on (the stylesheet links the
 * CDN, which the app's CSP does not allow and a print must not depend on).
 */
export const GGOP_BOOTSTRAP_SUBSET = `
body { font: 10pt/1.4 Arial, Helvetica, sans-serif; color: #111; margin: 0; }
.container { width: auto; }
div.background { width: auto !important; margin: 0 !important; }
.row { display: flex; flex-wrap: wrap; margin: 0 -6px; }
.row > [class*="col-"] { padding: 0 6px; box-sizing: border-box; }
.col-sm-1{width:8.333%}.col-sm-2{width:16.667%}.col-sm-3{width:25%}.col-sm-4{width:33.333%}.col-sm-5{width:41.667%}.col-sm-6{width:50%}
.col-sm-7{width:58.333%}.col-sm-8{width:66.667%}.col-sm-9{width:75%}.col-sm-10{width:83.333%}.col-sm-11{width:91.667%}.col-sm-12{width:100%}
.text-center{text-align:center}.text-right{text-align:right}
.panel { border: 1px solid #999; margin-top: 10px; break-inside: avoid; }
.panel-heading { background: #bedefc; border-bottom: 1px solid #999; padding: 4px 6px; font-weight: 600; }
.panel-body { padding: 6px; background: #fff; }
.panel-title { margin: 0; font-size: 10.5pt; }
dl { margin: 0; }
.dl-horizontal dt { float: left; width: 11em; clear: left; text-align: right; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dl-horizontal dd { margin-left: 12em; }
.dl-horizontal:after, .row:after { content: ""; display: table; clear: both; }
h2 { font-size: 14pt; margin: 4px 0 8px; } h2 small { font-size: 9pt; color: #555; font-weight: normal; }
table { border-collapse: collapse; } .table { width: 100%; } .table td, .table th { border-top: 1px solid #ddd; padding: 3px 5px; text-align: left; vertical-align: top; }
`;

/** Screen document for the viewer iframe. */
export function ggopScreenHtml(rendered: GgopRendered): string {
  return `<!doctype html><html lang="da"><head><meta charset="utf-8"><style>${rendered.css}\n${GGOP_BOOTSTRAP_SUBSET}\nbody{padding:8px}</style></head><body>${rendered.html}</body></html>`;
}

export type GgopPrintLabels = { cpr: string; page: string; of: string; printedAt: string };

/**
 * Print document (GGOP-M 2.6, GGOP-S 3.5.12): the plan's heading and the patient's name
 * and CPR at the top of every printed page (a <thead> the browser repeats per page),
 * "Side n af m" at the bottom (@page margin box).
 */
export function ggopPrintHtml(input: {
  rendered: GgopRendered;
  heading: string;
  patientName: string;
  cprText: string;
  labels: GgopPrintLabels;
}): string {
  const { rendered, heading, patientName, cprText, labels } = input;
  return `<!doctype html>
<html lang="da"><head><meta charset="utf-8"><title>${escapeHtml(heading)} — ${escapeHtml(patientName)}</title>
<style>
${rendered.css}
${GGOP_BOOTSTRAP_SUBSET}
@page {
  size: A4;
  margin: 15mm 12mm 16mm 12mm;
  @bottom-right { content: ${cssString(labels.page + ' ')} counter(page) ${cssString(' ' + labels.of + ' ')} counter(pages); font: 9pt Arial, sans-serif; color: #444; }
}
table.page { width: 100%; border-collapse: collapse; }
thead .running { border-bottom: 1px solid #999; padding: 0 0 4px; font-size: 9pt; display: flex; justify-content: space-between; gap: 12px; }
thead td { padding-bottom: 8px; }
div.background { background: #fff !important; border: 0 !important; padding: 0 !important; }
</style></head>
<body>
<table class="page">
<thead><tr><td><div class="running"><span><strong>${escapeHtml(heading)}</strong></span><span>${escapeHtml(patientName)} · ${escapeHtml(labels.cpr)} <strong>${escapeHtml(cprText)}</strong></span></div></td></tr></thead>
<tbody><tr><td>
<div class="meta" style="color:#555;font-size:9pt;margin-bottom:6px">${escapeHtml(labels.printedAt)}</div>
${rendered.html}
</td></tr></tbody>
</table>
</body></html>`;
}

/** Headline for a plan type (GGOP-M 3.1/3.3/3.5): the receiver sees which plan it is. */
export const GGOP_PLAN_HEADINGS: Record<string, string> = {
  almen: 'Almen genoptræning',
  specialiseret: 'Genoptræning på specialiseret niveau',
  rehabilitering: 'Rehabilitering på specialiseret niveau',
};

export function ggopHeading(planType: string | null | undefined): string {
  return (planType && GGOP_PLAN_HEADINGS[planType]) || 'Genoptræningsplan';
}
